// src/components/ui/index.js
// Gemeinsame UI-Bausteine von fluent!. Screens importieren von hier, z. B.
//   import { Button, OptionCard } from "../components/ui";
// Aussehen steht in ui.css (nur Tokens aus src/index.css), nicht in Inline-Styles.
import React, { forwardRef } from "react";
import "./ui.css";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

/**
 * variant: "primary" (Hauptaktion) | "secondary" | "ghost" | "right" | "wrong" | "danger"
 * size: "md" | "sm"; block: volle Breite
 */
export function Button({
  variant = "primary",
  size = "md",
  block = false,
  type = "button",
  className,
  ...rest
}) {
  return (
    <button
      type={type}
      className={cx(
        "ui-btn",
        `ui-btn--${variant}`,
        size === "sm" && "ui-btn--sm",
        block && "ui-btn--block",
        className
      )}
      {...rest}
    />
  );
}

/**
 * Auswählbare Kachel (Sprache, Tempo, Antwortmöglichkeit). hint: kleine zweite Zeile.
 * tone: "right" | "wrong" nach dem Prüfen (überschreibt active)
 */
export function OptionCard({ active = false, tone, label, hint, className, children, ...rest }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={cx(
        "ui-option",
        active && !tone && "ui-option--active",
        tone && `ui-option--${tone}`,
        className
      )}
      {...rest}
    >
      {label != null && <span className="ui-option__label">{label}</span>}
      {hint != null && <span className="ui-option__hint">{hint}</span>}
      {children}
    </button>
  );
}

/**
 * Elemente untereinander mit gleichem Abstand. gap: Stufe 1–6 der --fluent-space-Tokens.
 * row: nebeneinander, alle gleich breit; equal: untereinander, alle gleich hoch
 */
export function Stack({ gap = 3, row = false, equal = false, className, style, ...rest }) {
  return (
    <div
      className={cx("ui-stack", row && "ui-stack--row", equal && "ui-stack--equal", className)}
      style={{ gap: `var(--fluent-space-${gap})`, ...style }}
      {...rest}
    />
  );
}

/**
 * Umschaltleiste für wenige Werte (z. B. Wörter pro Sitzung).
 * options: [{ value, label }]; label: Überschrift darüber (optional)
 */
export function Segmented({ options, value, onChange, label, className }) {
  const activeIndex = options.findIndex((opt) => opt.value === value);
  return (
    <div className={className}>
      {label != null && <span className="ui-label">{label}</span>}
      <div className="ui-segmented" role="radiogroup" aria-label={label}>
        {/* Weißer Schieber hinter der Auswahl, gleitet per CSS-Transition zur neuen Position.
            Breite = (Innenbreite − Lücken) / Anzahl; Verschiebung = Index × (Breite + Lücke) */}
        {activeIndex >= 0 && (
          <span
            className="ui-segmented__thumb"
            aria-hidden="true"
            style={{
              width: `calc((100% - ${options.length + 1} * var(--fluent-space-1)) / ${options.length})`,
              transform: `translateX(calc(${activeIndex} * (100% + var(--fluent-space-1))))`,
            }}
          />
        )}
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              className={cx("ui-segmented__item", active && "ui-segmented__item--active")}
              onClick={() => onChange(opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Infobox/Karte. variant: "default" (weiß) | "muted" (Nebel); raised: leichter Schatten */
export function Panel({ variant = "default", raised = false, className, ...rest }) {
  return (
    <div
      className={cx(
        "ui-panel",
        variant === "muted" && "ui-panel--muted",
        raised && "ui-panel--raised",
        className
      )}
      {...rest}
    />
  );
}

/** Kleine Abschnitts-Überschrift in Großbuchstaben (z. B. „Aufgaben“). */
export function Eyebrow({ className, ...rest }) {
  return <div className={cx("ui-eyebrow", className)} {...rest} />;
}

/** Titel oben auf jedem Screen, optional mit Frage/Untertitel darunter. center: mittig */
export function ScreenHeader({ title, subtitle, center = false, className }) {
  return (
    <header className={cx("ui-header", center && "ui-header--center", className)}>
      <h1 className="ui-header__title">{title}</h1>
      {subtitle != null && <p className="ui-header__subtitle">{subtitle}</p>}
    </header>
  );
}

/**
 * value: 0–1. tone: "blue" | "right" | "wrong" | "exp" | "level1"–"level5"
 * label/valueLabel: optionale Beschriftung links/rechts über dem Balken
 * gain: optional 0–1, hellerer Abschnitt bis zu diesem Wert (z. B. neu gewonnene EXP, die sich gleich auffüllen)
 */
export function ProgressBar({ value, tone = "blue", label, valueLabel, gain, className }) {
  const pct = Math.max(0, Math.min(1, Number(value) || 0)) * 100;
  const gainPct = gain != null ? Math.max(0, Math.min(1, Number(gain) || 0)) * 100 : null;
  return (
    <div className={className}>
      {(label != null || valueLabel != null) && (
        <div className="ui-progress__label">
          <span>{label}</span>
          <span>{valueLabel}</span>
        </div>
      )}
      <div
        className="ui-progress__track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
      >
        {gainPct != null && (
          <div className="ui-progress__gain" style={{ width: `${gainPct}%` }} />
        )}
        <div
          className={cx("ui-progress__fill", tone !== "blue" && `ui-progress__fill--${tone}`)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** Eingabefeld. status: "correct" | "almost" | "wrong" | undefined */
export const TextInput = forwardRef(function TextInput({ status, className, ...rest }, ref) {
  return (
    <input
      ref={ref}
      className={cx("ui-input", status && `ui-input--${status}`, className)}
      {...rest}
    />
  );
});

/**
 * Auswahlfeld (Dropdown). options: [{ value, label }];
 * placeholder: grauer erster Eintrag, solange nichts gewählt ist (value "")
 */
export const Select = forwardRef(function Select(
  { options, placeholder, value, className, ...rest },
  ref
) {
  return (
    <div className={cx("ui-select", !value && placeholder != null && "ui-select--placeholder", className)}>
      <select ref={ref} className="ui-input" value={value} {...rest}>
        {placeholder != null && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
});

/** Ablage für Wortkärtchen. variant: "answer" (Antwort) | "pool" (Vorrat); empty: Hinweis, wenn leer */
export function TileTray({ variant = "pool", empty, className, children, ...rest }) {
  const hasChildren = React.Children.count(children) > 0;
  return (
    <div className={cx("ui-tray", `ui-tray--${variant}`, className)} {...rest}>
      {children}
      {!hasChildren && empty != null && <span className="ui-tray__empty">{empty}</span>}
    </div>
  );
}

/**
 * Wortkärtchen. empty: freier Platz in der Antwort-Ablage;
 * tone: "right" | "wrong" nach dem Prüfen (gilt auch für leere Plätze)
 */
export function Tile({ empty = false, tone, className, children, ...rest }) {
  return (
    <button
      type="button"
      className={cx(
        "ui-tile",
        empty && !tone && "ui-tile--empty",
        tone && `ui-tile--${tone}`,
        className
      )}
      aria-label={empty ? "Freier Platz" : undefined}
      {...rest}
    >
      {empty ? " " : children}
    </button>
  );
}

/** Kurze Rückmeldung (z. B. unter einem Formular). tone: "info" | "error" | "success" */
export function Note({ tone = "info", className, ...rest }) {
  return (
    <p
      role={tone === "error" ? "alert" : "status"}
      className={cx("ui-note", tone !== "info" && `ui-note--${tone}`, className)}
      {...rest}
    />
  );
}

/** Kleine Pille. tone: "neutral" | "blue" | "sun" | "mint" | "coral" */
export function Badge({ tone = "neutral", className, ...rest }) {
  return (
    <span className={cx("ui-badge", tone !== "neutral" && `ui-badge--${tone}`, className)} {...rest} />
  );
}

// Einfache Linien-Symbole (24×24, Strichstärke 2, Farbe = Textfarbe) statt Emojis
const ICON_PATHS = {
  users: [
    "M9 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z",
    "M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6",
    "M16 4.8a3.3 3.3 0 0 1 0 6.4",
    "M17.5 14.3c2.4.6 4 2.6 4 5.7",
  ],
  flame: ["M12 3c.8 3.2 5 5.4 5 10.2a5 5 0 0 1-10 0c0-2.3 1-3.8 2.2-4.9.2 1.8 1 2.9 2.1 3.2-.4-3.1.3-5.8.7-8.5z"],
  shield: ["M12 3l7.5 3v5.5c0 4.4-3.1 8-7.5 9.5-4.4-1.5-7.5-5.1-7.5-9.5V6z"],
  bolt: ["M13 2.5L4.5 13.5h6.5l-1 8 8.5-11h-6.5z"],
  counter: ["M9 14L4 9l5-5", "M4 9h10.5a5.5 5.5 0 0 1 0 11H11"],
  medal: ["M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12z", "M8.5 14l-1.5 7 5-3 5 3-1.5-7"],
  check: ["M5 12.5l4.5 4.5L19 7.5"],
  cross: ["M6.5 6.5l11 11", "M17.5 6.5l-11 11"],
  lock: [
    "M7 11h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2z",
    "M8 11V7.5a4 4 0 0 1 8 0V11",
  ],
};

/**
 * Symbol. name: "users" | "flame" | "shield" | "bolt" | "counter" | "medal" | "check" | "cross" | "lock".
 * Größe folgt der Schriftgröße (1em); label: Text für Screenreader, sonst nur Deko
 */
export function Icon({ name, label, className }) {
  const paths = ICON_PATHS[name] || [];
  return (
    <svg
      className={cx("ui-icon", className)}
      viewBox="0 0 24 24"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
